/**
 * Media CDN image URLs — WHICH derivative to ask for, and in what order to fall back.
 *
 * Pure: no redux, no env, no platform APIs, no React. Callers bring their own base URL or
 * their own URL builder, because the two consumers get it from different places — the
 * customer display is handed `staticMediaBaseUrl` in its payload, while the kiosk reads env
 * + `businessData.staticFolder` through `helpers/catalogueHelpers`'s platform-split
 * `getImageUrl`. What both need to agree on is the ORDER, and that is what lives here.
 *
 * ## Why an order and not a size
 *
 * The CDN stores several derivatives per upload under `images/<mid>/`. Only
 * `original.<ext>` keeps the upload as it was: every sized derivative is written by sharp's
 * `.resize(w, h)`, which defaults to `fit: "cover"` and therefore CENTER-CROPS to that box.
 * `large` is 110x110 — fine for a POS row, and on a customer display or a 426px kiosk tile
 * it reads as both cropped and blurry.
 *
 * So the best candidate is `original`, and the sized ones stay on as fallbacks, largest
 * first, so an upload whose `original` is missing still renders instead of a broken image.
 * Stepping through them is a UI concern: `@rista/core/hooks/useImageUrlFallback`.
 *
 * Extracted from `customerDisplay` (2026-09-09), which had the only copy, when the kiosk
 * turned out to need the same ordering for its item artwork.
 */

/**
 * Every sized derivative the CDN writes, LARGEST FIRST. Each one is center-cropped to its
 * own box by sharp's `fit: "cover"`.
 *
 * A caller picks where on this ladder to start (`from`) and everything smaller follows as
 * fallbacks. Starting small is not a defect to be fixed everywhere: the kiosk's category rail
 * draws a 42px icon and its cart rows a 56px thumbnail, and making those fetch a 720x480 is
 * a straight waste of bandwidth on in-restaurant hardware. What was wrong before was that
 * every surface got `small` — including a 320x400 tile.
 */
export const IMAGE_SIZE_LADDER = ["720x480", "450x300", "xl", "large", "small"];

export type ImageSize = (typeof IMAGE_SIZE_LADDER)[number];

/**
 * The rung named, plus every smaller one. An unknown name falls back to the whole ladder
 * rather than to nothing — a typo should cost a larger download, not a missing picture.
 */
export function sizeLadderFrom(from?: ImageSize): string[] {
  if (!from) return IMAGE_SIZE_LADDER;
  const start = IMAGE_SIZE_LADDER.indexOf(from);
  return start < 0 ? IMAGE_SIZE_LADDER : IMAGE_SIZE_LADDER.slice(start);
}

/** Extensions a campaign upload's `name` field carries when it's a video. */
const VIDEO_EXTENSIONS = new Set(["mp4", "mov", "m4v", "webm"]);

/** Lowercased extension of an uploaded file's `name`, e.g. "burger.JPG" -> "jpg". */
export function getFileExtension(name: unknown): string | undefined {
  if (typeof name !== "string") return undefined;
  const parts = name.split(".");
  return parts.length > 1 ? parts.pop()?.toLowerCase() : undefined;
}

/** Video vs image, inferred from the uploaded file's name — the campaign API sends no explicit media-type field. */
export function getMediaKind(name: unknown): "image" | "video" {
  const extension = getFileExtension(name);
  return extension && VIDEO_EXTENSIONS.has(extension) ? "video" : "image";
}

/**
 * `original` is the ONLY key stored with an extension (`original.${format}`), so it has to be
 * reconstructed from the upload's `name`. The backend records jpeg under whichever spelling
 * the source carried, so both are tried.
 *
 * Empty when the caller has no name to work from — a catalogue item carries an
 * `imageDataId` and nothing else, so a kiosk gets the sized fallbacks alone. That is still
 * `720x480` first rather than the `small` it used to request.
 */
export function getOriginalImageExtensions(name: unknown): string[] {
  const extension = getFileExtension(name);
  if (!extension) return [];
  if (extension === "jpg" || extension === "jpeg") return ["jpg", "jpeg"];
  return [extension];
}

/**
 * The candidate list, best first, for a caller that already has a URL builder — the kiosk's
 * `getImageUrl(imageId, type)`, whose `type` is exactly this function's `size` argument.
 *
 * Empty strings are dropped: `getImageUrl` returns `""` when the business has no
 * `staticFolder` or the item no image, and an `<Image source={{ uri: "" }} />` is a load
 * error on both platforms rather than a no-op.
 *
 * ⚠️ Each size is passed through an arrow rather than handing `toUrl` straight to `.map`.
 * `Array.prototype.map` calls its callback with `(value, index, array)`, and the obvious
 * `.map(getImageUrl)` would therefore call it as `getImageUrl(size, index, array)` — whose
 * second parameter IS the size. Every fallback would silently resolve to `images/720x480/0`.
 */
export interface ImageCandidateOptions {
  /**
   * The upload's file name, when the caller has one — it is the only way to reach the
   * uncropped `original`, whose stored key carries an extension. A catalogue item has an
   * `imageDataId` and no name, so it gets the sized ladder alone.
   */
  name?: unknown;
  /** Where to start on `IMAGE_SIZE_LADDER`. Default: the largest. */
  from?: ImageSize;
}

export function imageUrlCandidates(
  toUrl: (size: string) => string,
  { name, from }: ImageCandidateOptions = {},
): string[] {
  return [
    ...getOriginalImageExtensions(name).map((extension) => toUrl(`original.${extension}`)),
    ...sizeLadderFrom(from).map((size) => toUrl(size)),
  ].filter((url): url is string => Boolean(url));
}

/**
 * The same list for a caller that has the base instead of a builder —
 * `${baseURL}/${businessData.staticFolder}`, the customer display's `staticMediaBaseUrl`.
 *
 * Built on `imageUrlCandidates` so there is one ordering rather than two that agree today.
 */
export function buildImageUrls(
  mid: string,
  staticMediaBaseUrl: string,
  name?: unknown,
): string[] {
  const base = `${staticMediaBaseUrl}images/${mid}`;
  // A campaign slide fills a whole display, so it starts at the top of the ladder — which is
  // the default, spelled out here because it is a decision rather than an omission.
  return imageUrlCandidates((size) => `${base}/${size}`, { name });
}
